import React from "react";
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, Button, Paper,
} from "@mui/material";

import {
    couponDisplayLabel,
    rewardDisplayLabel,
    rewardProductsList,
    entitlementStatusLabel,
    entitlementStatusColor,
    canAdminTransitionCoupon,
} from "../../utils/customerEntitlementLabels";

const CATALOG_REMOVED_LABEL = "Modèle retiré du catalogue";

function StatusCell({ entitlement, catalogStatusLabels }) {
    return (
        <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
            <Chip
                label={entitlementStatusLabel(entitlement, catalogStatusLabels)}
                color={entitlementStatusColor(entitlement?.status)}
                size="small"
                sx={{ fontWeight: 600, fontSize: 11 }}
            />
            {entitlement?.catalog_removed && (
                <Chip
                    label={CATALOG_REMOVED_LABEL}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ fontWeight: 600, fontSize: 10, height: 20 }}
                />
            )}
        </Box>
    );
}

function RewardRow({ reward, rewardDefs, catalogStatusLabels }) {
    const products = rewardProductsList(reward);
    return (
        <Box key={String(reward.id)} mb={0.75}>
            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                <Typography fontSize={12} fontWeight={600}>{rewardDisplayLabel(reward, rewardDefs)}</Typography>
                <Chip
                    label={entitlementStatusLabel(reward, catalogStatusLabels)}
                    size="small"
                    color={entitlementStatusColor(reward?.status)}
                    sx={{ fontWeight: 600, fontSize: 10, height: 20 }}
                />
                {reward?.catalog_removed && (
                    <Chip label={CATALOG_REMOVED_LABEL} size="small" color="warning" variant="outlined"
                        sx={{ fontWeight: 600, fontSize: 9, height: 18 }} />
                )}
            </Box>
            {products.length > 0 && (
                <Box pl={1} mt={0.25} display="flex" flexDirection="column" gap={0.25}>
                    {products.map((p, idx) => (
                        <Box key={idx} display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                            <Typography fontSize={11} color="text.secondary">
                                · {p.name || p.product_name || "Produit"}
                                {p.quantity != null ? ` ×${p.quantity}` : ""}
                            </Typography>
                            {p.catalog_removed && (
                                <Chip label="retiré" size="small" color="warning" variant="outlined"
                                    sx={{ fontWeight: 600, fontSize: 9, height: 16 }} />
                            )}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

/**
 * Tableau coupons + rewards (GET …/coupons-with-rewards).
 * @param {boolean} showActions — boutons PATCH statut (ProfilFidelite)
 */
const CustomerCouponsEntitlementsTable = ({
    items = [],
    rewardDefs = {},
    catalogStatusLabels,
    showActions = false,
    onStatusChange,
    saving = false,
    couponActionId = null,
    compact = false,
}) => {
    const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("fr-FR") : "–");

    return (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8ecf1" }}>
            <Table size={compact ? "small" : "medium"}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8f9ff" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Coupon</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Obtenu le</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Expire le</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Utilisé le</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Avantages</TableCell>
                        {showActions && <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={showActions ? 7 : 6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                                Aucun coupon trouvé.
                            </TableCell>
                        </TableRow>
                    ) : items.map((item) => {
                        const coupon = item?.coupon;
                        const rewards = Array.isArray(item?.rewards) ? item.rewards : [];
                        const couponId = coupon?.id ? String(coupon.id) : "";
                        const actionsDisabled = coupon?.admin_actions_enabled === false;
                        const isBusy = saving && couponActionId === couponId;
                        return (
                            <TableRow key={couponId || Math.random()} hover>
                                <TableCell>
                                    <Typography fontWeight={700} fontSize={compact ? 13 : 14}>
                                        {couponDisplayLabel(coupon)}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <StatusCell entitlement={coupon} catalogStatusLabels={catalogStatusLabels} />
                                </TableCell>
                                <TableCell sx={{ fontSize: compact ? 13 : 14 }}>{fmtDate(coupon?.issued_at)}</TableCell>
                                <TableCell sx={{ fontSize: compact ? 13 : 14 }}>
                                    {coupon?.expires_at ? fmtDate(coupon.expires_at) : "Jamais"}
                                </TableCell>
                                <TableCell sx={{ fontSize: compact ? 13 : 14 }}>{fmtDate(coupon?.used_at)}</TableCell>
                                <TableCell>
                                    {rewards.length === 0 ? "–" : rewards.map((r) => (
                                        <RewardRow key={String(r.id)} reward={r} rewardDefs={rewardDefs}
                                            catalogStatusLabels={catalogStatusLabels} />
                                    ))}
                                </TableCell>
                                {showActions && (
                                    <TableCell align="right">
                                        {couponId && (
                                            <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-end">
                                                <Button
                                                    size="small" variant="contained"
                                                    disabled={actionsDisabled || isBusy || !canAdminTransitionCoupon(coupon, "USED")}
                                                    onClick={() => onStatusChange?.(couponId, "USED")}
                                                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, fontSize: 11,
                                                        ...(actionsDisabled ? { opacity: 0.45 } : {}) }}
                                                >
                                                    Utiliser
                                                </Button>
                                                <Button
                                                    size="small" variant="outlined"
                                                    disabled={actionsDisabled || isBusy || !canAdminTransitionCoupon(coupon, "ISSUED")}
                                                    onClick={() => onStatusChange?.(couponId, "ISSUED")}
                                                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, fontSize: 11,
                                                        ...(actionsDisabled ? { opacity: 0.45 } : {}) }}
                                                >
                                                    Rouvrir
                                                </Button>
                                                <Button
                                                    size="small" variant="outlined" color="error"
                                                    disabled={actionsDisabled || isBusy || !canAdminTransitionCoupon(coupon, "EXPIRED")}
                                                    onClick={() => onStatusChange?.(couponId, "EXPIRED")}
                                                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, fontSize: 11,
                                                        ...(actionsDisabled ? { opacity: 0.45 } : {}) }}
                                                >
                                                    Expirer
                                                </Button>
                                            </Box>
                                        )}
                                    </TableCell>
                                )}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CustomerCouponsEntitlementsTable;
